import {
	AccountFeed,
	AccountsList,
	// BillingGroup,
	BillingGroups,
	BnRedirect,
	Contacts,
	CustomerResponsibles,
	CustomerUser,
	ThreeSixtyStoreState,
	Users,
	ZendeskCases,
	Account,
	VismaUpsellResponse,
	OrderUpsellProductsRequest,
	VismaRestkontoDownloadRequest,
	PageFiltersTypes,
	UserConfig,
	InnsalgOppslag,
	InnsalgSvar,
	InnsalgSvarResultat,
} from 'common/types/store/threeSixtyTypes';
import {showError, showSuccess} from '@phonectas/unifon-ui-kit';
import {getI18n} from 'react-i18next';
import {
	INNSALG_ENDPOINTS,
	InnsalgApiError,
	innsalgApiCall,
	sendSmsCall,
	threeSixtyApiCall,
	ThreeSixtyApiError,
	vismaChangeDueDate,
} from 'services/threeSixtyApi';
import useUserStore from 'common/stores/user';
import {ChangeDueDateRequest, ChangeDueDateResponse, SendSmsResponse} from 'services/types';
import {LIMIT_PAYLOAD, USERS_DEFAULT_FILTERS, USERS_NO_FILTERS} from 'common/constants';
import {AccountList, ConfigTemplate, ReportIncident} from 'common/stores/types';
import {getAliasHeader} from 'services/constants';
import {vismaApiUrl} from 'services/services';
import {TemplateType} from 'common/components/TrustpilotDialog';
import {DisplayPreferences, UserPreferences} from './profileModel';

type SetStateFunction = (updater: (state: ThreeSixtyStoreState) => Partial<ThreeSixtyStoreState>) => void;

class ThreeSixtyStoreModel {
	private set: SetStateFunction;
	// Bumped on every innsalg lookup/reset so a slow answer for a previous customer is ignored
	private innsalgRequestId = 0;
	// Bumped on every upsell user lookup so a slow answer for a previously selected user is ignored
	private upsellRequestId = 0;
	// Bumped on every account search / feed fetch so a slow answer for an older query, filter or customer is ignored
	private accountListRequestId = 0;
	private accountFeedRequestId = 0;
	// Requests currently holding isLoading, see startLoading()
	private pendingLoads = 0;
	// The user config request in flight, shared by callers that only need the config loaded
	private userConfigRequest: Promise<void> | null = null;
	public defaultState: ThreeSixtyStoreState;

	constructor(set: SetStateFunction) {
		this.set = set;
	
		// Initialize the default state as a plain object
		this.defaultState = {
			isLoading: false,
			isAccFeedLoading: false,
			isUpsellLoading: false,
			isVismaLoading: false,
			accounts: null,
			accountNotFound: false,
			activeAccount: null,
			accountFeed: null,
			activeRow: null,
			activeRowVisma: null,
			accountTables: {
				billingGroups: null,
				contacts: null,
				users: null,
				zendeskCases: null,
				isLoadingTable: false,
				parentId: null,
				visma: null,
			},
			queryResaults: null,
			currentUser: null,
			bnRedirect: {
				account: null,
				user: null,
			},
			customerResponibles: null,
			activeCustomerResp: null,
			configTemplates: null,
			smsDetails: null,
			smsTrustpilotDetails: null,
			byttDetails: null,
			cdRatorOwnerSelector: null,
			upsellDetails: null,
			innsalg: null,
			innsalgOrgnr: null,
			isInnsalgLoading: false,
			userConfig: null,
			pageFilters: {
				eventFeed: null,
				smsFeed: null,
			},
		};
	}

	// Utility to update the state
	updateState(updater: (state: ThreeSixtyStoreState) => Partial<ThreeSixtyStoreState>): void {
		this.set((state) => {
			const updatedState = {
				...state,
				...updater(state),
			};
			return updatedState;
		});
	}
	// isLoading stays true while any request that uses it is running, not only until the first one finishes
	private startLoading(): () => void {
		this.pendingLoads++;
		this.updateState(() => ({isLoading: true}));
		let done = false;
		return () => {
			if (done) return;
			done = true;
			this.pendingLoads--;
			this.updateState(() => ({isLoading: this.pendingLoads > 0}));
		};
	}
	updatePageFilters(filters: Partial<PageFiltersTypes>): void {
		this.updateState((state) => ({
			pageFilters: {
				...state.pageFilters,
				...filters,
			},
		}));
	}
	resetState(): void {
		this.set(() => ({...this.defaultState}));
	}
	resetAccounts(): void {
		this.updateState(() => ({accounts: null}));
	}
	setBnRedirect(value: Partial<BnRedirect>): void {
		this.updateState((state) => ({
			bnRedirect: {
				...state.bnRedirect,
				...value,
			},
		}));
	}
	resetBnRedirect(): void {
		this.updateState(() => ({bnRedirect: {account: null, user: null}, innsalg: null, innsalgOrgnr: null}));
	}
	resetInnsalg(): void {
		this.innsalgRequestId++;
		this.updateState(() => ({innsalg: null, innsalgOrgnr: null}));
	}
	resetQueryResults(): void {
		this.updateState(() => ({queryResaults: null}));
	}
	setCurrentUser(user: string | null): void {
		this.updateState(() => ({currentUser: user}));
	}
	setActiveRow(row: CustomerUser | null): void {
		this.updateState(() => ({activeRow: row}));
	}
	// Fetch

	// Upsell user details
	async getUpsellUser(userId: number, onSuccess?: () => void): Promise<void> {
		const requestId = ++this.upsellRequestId;
		// Clear the previous user's details, so nothing is shown for the wrong user while loading or after an error
		this.updateState(() => ({isUpsellLoading: true, upsellDetails: null}));
		try {
			const response = await threeSixtyApiCall<VismaUpsellResponse>({
				endpoint: 'upsell/user',
				method: 'POST',
				body: {
					user_id: userId,
				},
			});
			if (requestId !== this.upsellRequestId) return;
			// if (response.ok === true)
			this.updateState(() => ({upsellDetails: response}));
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('getUpsellUser Error:', err);
		} finally {
			if (requestId === this.upsellRequestId) this.updateState(() => ({isUpsellLoading: false}));
		}
	}

	async postUpsellOrder({
		data,
		onSuccess,
	}: {
		data: OrderUpsellProductsRequest;
		onSuccess?: (() => void) | undefined | null;
	}): Promise<void> {
		this.updateState(() => ({isUpsellLoading: true}));
		try {
			await threeSixtyApiCall<OrderUpsellProductsRequest>({
				endpoint: 'upsell/order',
				method: 'POST',
				body: data,
			});
			showSuccess({
				message: getI18n().t('Upsell request sent', {returnObjects: false}) as string,
			});
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('postUpsellOrder Error:', err);
			showError({
				message: getI18n().t('Failed upsell request', {returnObjects: false}) as string,
			});
		} finally {
			this.updateState(() => ({isUpsellLoading: false}));
		}
	}

	async postUpsellSms({
		phoneNumber,
		onSuccess,
	}: {
		phoneNumber: string;
		onSuccess?: (() => void) | undefined | null;
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			await threeSixtyApiCall<OrderUpsellProductsRequest>({
				endpoint: 'upsell/order/sms',
				method: 'POST',
				body: {
					phone_number: phoneNumber,
				},
			});
			showSuccess({
				message: getI18n().t('Upsell request SMS sent', {returnObjects: false}) as string,
			});
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('postUpsellSms Error:', err);
			showError({
				message: getI18n().t('Failed upsell SMS request', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}

	// Innsalg (AI Medarbeider). Failing lookups must never block the agent, so errors are only logged
	async getInnsalg(orgnr: string): Promise<InnsalgOppslag | null> {
		const requestId = ++this.innsalgRequestId;
		try {
			const kundebehandler = useUserStore.getState().user?.email ?? '';
			const response = await innsalgApiCall<InnsalgOppslag>({
				endpoint: INNSALG_ENDPOINTS.lookup(orgnr, kundebehandler),
				method: 'GET',
			});
			if (requestId !== this.innsalgRequestId) return null;
			this.updateState(() => ({innsalg: response, innsalgOrgnr: orgnr}));
			return response;
		} catch (err) {
			console.error('getInnsalg Error:', err);
			if (requestId === this.innsalgRequestId) this.updateState(() => ({innsalg: null, innsalgOrgnr: null}));
			return null;
		}
	}

	async postInnsalgSvar({
		visningId,
		data,
		onSuccess,
	}: {
		visningId: string;
		data: InnsalgSvar;
		onSuccess?: (() => void) | undefined | null;
	}): Promise<void> {
		this.updateState(() => ({isInnsalgLoading: true}));
		try {
			const response = await innsalgApiCall<InnsalgSvarResultat>({
				endpoint: INNSALG_ENDPOINTS.answer(visningId),
				method: 'POST',
				body: data,
			});
			showSuccess({
				message: 'skjult_til' in response ?
					getI18n().t('Innsalg declined', {date: response.skjult_til, returnObjects: false}) as string :
					getI18n().t('Innsalg lead sent', {returnObjects: false}) as string,
			});
			this.updateState(() => ({innsalg: null, innsalgOrgnr: null}));
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('postInnsalgSvar Error:', err);
			// Planleggeren's message tells which field is wrong (400) or that it is already answered (409)
			showError({
				message: err instanceof InnsalgApiError && err.message ?
					err.message :
					getI18n().t('Failed to send innsalg answer', {returnObjects: false}) as string,
			});
		} finally {
			this.updateState(() => ({isInnsalgLoading: false}));
		}
	}

	// Fetch account list using threeSixtyApiCall
	async getAccountList(limit: number = LIMIT_PAYLOAD, search = '', sort = ''): Promise<void> {
		const requestId = ++this.accountListRequestId;
		const doneLoading = this.startLoading();
		try {
			const response = await threeSixtyApiCall<AccountsList>({
				endpoint: 'accounts/list',
				method: 'POST',
				body: {
					filters: {
						search_text: search,
					},
					limit,
					sort_mode: sort,
				},
			});
			if (requestId !== this.accountListRequestId) return;

			this.updateState(() => ({accounts: response}));
		} catch (err) {
			console.error('getAccountList Error:', err);
			if (requestId === this.accountListRequestId) this.updateState(() => ({accounts: null}));
		} finally {
			doneLoading();
		}
	}

	// Get account by ID or phone number
	async getAccount({
		id,
		phone,
		onSuccess,
	}: {
		id?: string | null;
		phone?: string | null;
		onSuccess?: (() => void) | undefined | null;
	}): Promise<void> {
		const endpoint = id
			? `account/id/${id}`
			: phone
				? `account/phone/${phone}`
				: null;

		if (!endpoint) {
			console.error('getAccount Error: either "id" or "phone" must be provided');
			return;
		}
		// A not-found from an earlier lookup must not show while this one loads
		const doneLoading = this.startLoading();
		this.updateState(() => ({accountNotFound: false}));
		try {
			const response = await threeSixtyApiCall<Account>({
				endpoint: endpoint,
				method: 'GET',
			});
			if (id) {
				this.updateState(() => ({activeAccount: response}));
			}
			if (phone) {
				this.updateState((state) => ({
					bnRedirect: {
						...state.bnRedirect,
						account: response,
					},
					accountNotFound: false,
				}));
			}
			if (onSuccess) {
				onSuccess();
			}
		} catch (err) {
			console.error('getAccount Error:', err);
			// An error status means not found, a network failure is only logged
			if (err instanceof ThreeSixtyApiError) {
				this.updateState(() => ({accountNotFound: true}));
				showError({
					message: getI18n().t('Account not found', {returnObjects: false}) as string,
				});
			}
		} finally {
			doneLoading();
		}
	}
	// get Account feed
	async getAccountFeed({
		id,
		feedType = '',
		limit = LIMIT_PAYLOAD,
		sort = '',
		search = '',
		isLoading = false,
	}:{
		id: string,
		feedType?: string | string[],
		limit: number,
		sort?: string,
		search?: string,
		isLoading?: boolean,
	}): Promise<void> {
		const requestId = ++this.accountFeedRequestId;
		// isLoading: true shows the page loader (scroll loading), otherwise the feed's own loader
		const doneLoading = isLoading ? this.startLoading() : null;
		if (!isLoading) {
			this.updateState(() => ({isAccFeedLoading: true}));
		}
		try {
			const response = await threeSixtyApiCall<AccountFeed>({
				endpoint: 'accountfeed/list',
				method: 'POST',
				body: {
					filters: {
						account_id: id,
						feed_types: feedType,
						search_text: search,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
			if (requestId === this.accountFeedRequestId) this.updateState(() => ({accountFeed: response}));
		} catch (err) {
			console.error('getAccountFeed Error:', err);
			if (requestId === this.accountFeedRequestId) this.updateState(() => ({accountFeed: null}));
		} finally {
			// Always clear this call's own flag: a newer call may use the other flag, and would leave this one stuck
			if (!isLoading) {
				this.updateState(() => ({isAccFeedLoading: false}));
			}
			doneLoading?.();
		}
	}

	// Account tables
	// get Billing groups
	async getBillingGroups({
		id,
		limit = LIMIT_PAYLOAD,
		sort = '',
		search = '',
	}: {
		id: string;
		limit?: number;
		sort?: string;
		search?: string;
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await threeSixtyApiCall<BillingGroups>({
				endpoint: 'billinggroups/list',
				method: 'POST',
				body: {
					filters: {
						account_id: id,
						search_text: search,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
			this.updateState((state) => ({
				accountTables: {
					...state.accountTables,
					billingGroups: response,
				},
			}));
		} catch (err) {
			console.error('getBillingGroups Error:', err);
			this.updateState((state) => ({accountTables: {...state.accountTables, billingGroups: null}}));
		} finally {
			doneLoading();
		}
	}
	// get Contacts
	async getContacts({
		id,
		limit = LIMIT_PAYLOAD,
		sort = '',
		search = '',
		lazy = false,
	}: {
		id: string;
		limit?: number;
		sort?: string;
		search?: string;
		lazy?: boolean;
	}): Promise<void> {
		// The lazy contact person lookup runs in the background, without the page loader
		const doneLoading = lazy ? null : this.startLoading();
		try {
			const response = await threeSixtyApiCall<Contacts>({
				endpoint: 'contacts/list',
				method: 'POST',
				body: {
					filters: {
						account_id: id,
						search_text: search,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
			if (lazy) {
				this.updateState(() => ({cdRatorOwnerSelector: response}));
			} else {
				this.updateState((state) => ({
					accountTables: {
						...state.accountTables,
						contacts: response,
					},
				}));
			}
		} catch (err) {
			console.error('getContacts Error:', err);
			if (lazy) {
				this.updateState(() => ({cdRatorOwnerSelector: null}));
			} else {
				this.updateState((state) => ({accountTables: {...state.accountTables, contacts: null}}));
			}
		} finally {
			doneLoading?.();
		}
	}
	// get Users
	async getUsers({
		id,
		limit = LIMIT_PAYLOAD,
		sort = 'statusAsc',
		search = '',
		statuses = true,
		phone = '',
		parentId = '',
		childCards = false,
	}: {
		id: string;
		limit?: number;
		sort?: string;
		search?: string;
		statuses?: boolean;
		phone?: string;
		parentId?: string;
		childCards?: boolean;
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await threeSixtyApiCall<Users>({
				endpoint: 'users/list',
				method: 'POST',
				body: {
					filters: {
						account_id: id,
						cdr_parent_id: parentId || null,
						include_child_cards: childCards || null,
						search_text: search,
						phone_number: phone || null,
						statuses: statuses ? USERS_DEFAULT_FILTERS : USERS_NO_FILTERS,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
			this.updateState((state) => {
				return {
					accountTables: {
						...state.accountTables,
						users: response,
					},
				};
			});
		} catch (err) {
			console.error('getUsers Error:', err);
			this.updateState((state) => ({accountTables: {...state.accountTables, users: null}}));
		} finally {
			doneLoading();

		}
	}
	// get user
	async getUser({
		phone,
		accountId,
		onSuccess,
	}: {
		phone: string;
		accountId: string;
		onSuccess?: (() => void) | undefined | null;
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await threeSixtyApiCall<Users>({
				endpoint: 'users/list',
				method: 'POST',
				body: {
					filters: {
						phone_number: phone,
					},
					limit: LIMIT_PAYLOAD,
				},
			});
			if (response.customer_users && response.customer_users.length > 0) {
				let user: CustomerUser | null = response.customer_users[0] || null;
	
				if (accountId) {
					const matchedUser = response.customer_users.find((u) => u.account_id === accountId);
					if (matchedUser) {
						user = matchedUser;
					}
				}
	
				this.updateState((state) => ({
					bnRedirect: {
						...state.bnRedirect,
						user: user,
					},
					activeRow: user,
				}));
	
				if (onSuccess) {
					onSuccess();
				}
			}
		} catch (err) {
			console.error('getUser Error:', err);
			this.updateState(() => ({accountNotFound: true}));
		} finally {
			doneLoading();
		}

	}

	async getUserParentId({
		limit = LIMIT_PAYLOAD,
		accountId,
		parent,
		includeChild = true,
	}: {
		limit?: number;
		accountId: string;
		parent: string;
		includeChild: boolean;
	}): Promise<void> {
		this.updateState((state) => ({
			accountTables: {
				...state.accountTables,
				isLoadingTable: true,
			},
		}));
	
		try {
			const response = await threeSixtyApiCall<Users>({
				endpoint: 'users/list',
				method: 'POST',
				body: {
					filters: {
						account_id: accountId,
						cdr_parent_id: parent,
						include_child_cards: includeChild,
					},
					limit: limit,
				},
			});
	
			if (response) {
				this.updateState((state) => ({
					accountTables: {
						...state.accountTables,
						parentId: response,
						isLoadingTable: false,
					},
				}));
			}
		} catch (err) {
			console.error('fetchUserParentId Error:', err);
		} finally {
			this.updateState((state) => ({
				accountTables: {
					...state.accountTables,
					isLoadingTable: false,
				},
			}));
		}
	}
	// get Zendesk cases
	async getZendesk({
		limit = LIMIT_PAYLOAD,
		id,
		search = '',
		sort = '',
	}: {
		limit?: number;
		id: string;
		search?: string;
		sort?: string;
	}): Promise<void> {
		this.updateState((state) => ({
			accountTables: {
				...state.accountTables,
				isLoadingTable: true,
			},
		}));
	
		try {
			const response = await threeSixtyApiCall<ZendeskCases>({
				endpoint: 'zendesk/cases/list',
				method: 'POST',
				body: {
					filters: {
						account_id: id,
						search_text: search,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
	
			if (response) {
				this.updateState((state) => ({
					accountTables: {
						...state.accountTables,
						zendeskCases: response,
						isLoadingTable: false,
					},
				}));
			}
		} catch (err) {
			console.error('getZendesk Error:', err);
		} finally {
			this.updateState((state) => ({
				accountTables: {
					...state.accountTables,
					isLoadingTable: false,
				},
			}));
		}
	}

	// account feed actions:
	async deleteEventComment(
		id: string,
		onSuccess?: (() => void) | undefined
	): Promise<void> {
		try {
			await threeSixtyApiCall<null>({
				endpoint: `comment/${id}`,
				method: 'DELETE',
			});
	
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('Comment deleted', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('deleteEventComment Error:', err);
			showError({
				message: getI18n().t('Error deleting comment', {returnObjects: false}) as string,
			});
		}
	}

	async addEventComment({
		id,
		comment,
		onSuccess,
	}: {
		id: string;
		comment: string;
		onSuccess?: (() => void) | undefined;
	}): Promise<void> {
		try {
			await threeSixtyApiCall<null>({
				endpoint: `account/${id}/comment`,
				method: 'POST',
				body: {
					comment: comment,
				},
			});
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('Comment added', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('addEventComment Error:', err);
			showError({
				message: getI18n().t('Error adding comment', {returnObjects: false}) as string,
			});
		}
	}
	// Major responsible list
	async majorResponsibleList({
		limit = LIMIT_PAYLOAD,
		search = '',
		sort = 'nameAsc',
	}: {
		limit?: number;
		search?: string;
		sort?: string;
	}): Promise<void> {
		const doneLoading = this.startLoading();

		try {
			const response = await threeSixtyApiCall<CustomerResponsibles>({
				endpoint: 'customerresps/list',
				method: 'POST',
				body: {
					filters: {
						search_text: search,
					},
					limit: limit,
					sort_mode: sort,
				},
			});
	
			if (response) {
				this.updateState(() => ({
					customerResponibles: response,
				}));
			}
		} catch (err) {
			console.error('majorResponsibleList Error:', err);
		} finally {
			doneLoading();
		}
	}

	async majorResponsibleSearch({
		entraId,
		search = '',
	}: {
		entraId: string;
		search?: string;
	}): Promise<void> {
		const doneLoading = this.startLoading();

		try {
			const response = await threeSixtyApiCall<AccountList>({
				endpoint: 'accounts/list',
				method: 'POST',
				body: {
					filters: {
						customer_resp_entra_id: entraId,
						search_text: search,
					},
					limit: LIMIT_PAYLOAD,
				},
			});
	
			if (response) {
				this.updateState(() => ({
					activeCustomerResp: response,
				}));
			}
		} catch (err) {
			console.error('majorResponsibleSearch Error:', err);
		} finally {
			doneLoading();
		}
	}

	// account satisfaction
	async setSatisfactionRating({
		accountId,
		body,
		onSuccess,
		onError,
	}: {
		accountId: string;
		body: string;
		onSuccess?: (() => void) | undefined;
		onError?: (() => void) | undefined;
	}): Promise<void> {
		try {
			await threeSixtyApiCall<null>({
				endpoint: `account/${accountId}/satisfactionrating`,
				method: 'POST',
				body: {
					satisfaction_rating: body,
				},
			});
	
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('Satisfaction set', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('setSatisfactionRating Error:', err);
			showError({
				message: getI18n().t('Error changing satisfaction', {returnObjects: false}) as string,
			});
			if (onError) {
				onError();
			}
		}
	}

	// send SMS
	async sendSms({
		number,
		text,
		onSuccess,
	}: {
		number: string;
		text: string;
		onSuccess?: (() => void) | undefined;
	}): Promise<void> {
		try {
			await sendSmsCall<SendSmsResponse>({
				body: {
					destination: number.toString(),
					message: text,
					sender: 'Unifon',
				},
			});
	
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('SMS sent', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('sendSms Error:', err);
			showError({
				message: getI18n().t('Error sending SMS', {returnObjects: false}) as string,
			});
		}
	}

	// Send TrustPilot Review
	async sendTrustPilotReview({
		data,
		onSuccess,
	}: {
		data: TemplateType;
		onSuccess?: (() => void) | undefined;
	}): Promise<void> {
		try {
			await threeSixtyApiCall<null>({
				endpoint: 'trustpilot/sendreview',
				method: 'POST',
				body: {
					account_id: data.account_id,
					consumer_name: data.consumer_name,
					receiver_mail: data.receiver_mail,
					user_id: data.user_id || '',
				},
			});
	
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('Review request sent', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('sendTrustPilotReview Error:', err);
			showError({
				message: getI18n().t('Error sending review request', {returnObjects: false}) as string,
			});
		}
	}

	// Get Config Templates
	async getConfigTemplates(identifier = 'utland_kategorier', invoiceNumber?: string, clientNumber?: number): Promise<void> {
		// The utland_kategorier lookup runs in the background, without the page loader
		let doneLoading: (() => void) | null = null;
		try {
			let response: ConfigTemplate;
	
			if (identifier === 'utland_kategorier') {
				response = await threeSixtyApiCall<ConfigTemplate>({
					endpoint: 'config/templates',
					method: 'POST',
					body: {identifier},
				});
	
				this.updateState(() => ({
					configTemplates: response as ConfigTemplate,
				}));
			} else if (identifier === 'sms_invoice') {
				doneLoading = this.startLoading();
				response = await threeSixtyApiCall<ConfigTemplate>({
					endpoint: 'config/templates',
					method: 'POST',
					body: {
						client_number: clientNumber,
						identifier: 'sms_invoice',
						invoice_number: invoiceNumber,
					},
				});
	
				this.updateState(() => ({
					smsDetails: Array.isArray(response) ? response : [response],
				}));
			} else if (identifier === 'trustpilot_sms') {
				doneLoading = this.startLoading();
				response = await threeSixtyApiCall<ConfigTemplate>({
					endpoint: 'config/templates',
					method: 'POST',
					body: {
						identifier: 'trustpilot_sms',
					},
				});
				this.updateState(() => ({
					smsTrustpilotDetails: Array.isArray(response) ? response[0] : response,
				}));
			} else if (identifier === 'bytt_sms') {
				doneLoading = this.startLoading();
				response = await threeSixtyApiCall<ConfigTemplate>({
					endpoint: 'config/templates',
					method: 'POST',
					body: {
						identifier: 'bytt_sms',
					},
				});
				this.updateState(() => ({
					byttDetails: Array.isArray(response) ? response[0] : response,
				}));
			}

		} catch (err) {
			console.error('getConfigTemplates Error:', err);
		} finally {
			doneLoading?.();
		}
	}

	// Report Incident
	async reportIncident({
		data,
		onSuccess,
	}: {
		data: Record<string, unknown>;
		onSuccess?: (() => void) | undefined;
	}): Promise<void> {
		try {
			await threeSixtyApiCall<ReportIncident>({
				endpoint: 'incident',
				method: 'POST',
				body: data ?  data : undefined,
			});

			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('Incident reported', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('reportIncident Error:', err);
			showError({
				message: getI18n().t('Error reporting incident', {returnObjects: false}) as string,
			});
		}
	}
	// VISMA
	// Get Visma List
	async getVismaList({
		id,
		page = 1,
		pageSize = 50,
		status = 'OPEN',
		billingGroups = [],
	}: {
		id: string;
		page?: number;
		pageSize?: number;
		status?: string;
		billingGroups?: string[];
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await fetch(`${vismaApiUrl}invoice/list`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify({
					account_id: id,
					page: page,
					page_size: pageSize,
					status: status,
					billing_groups: billingGroups || [],
				}),
			});
			// The error body is not always JSON (e.g. a 502 from the proxy)
			const json = await response.json().catch(() => ({}));
			if (response.ok) {
				this.updateState((state) => ({
					accountTables: {
						...state.accountTables,
						visma: json,
					},
				}));
			} else {
				const reason = typeof json?.message === 'string' && json.message ? `: ${json.message}` : '';
				showError({
					message: `${getI18n().t('Error fetching from visma', {returnObjects: false}) as string}${reason}`,
				});
				this.updateState((state) => ({
					accountTables: {
						...state.accountTables,
						visma: null,
					},
				}));
			}
		} catch (err) {
			console.error('getVismaList Error:', err);
			showError({
				message: getI18n().t('Error fetching from visma', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}

	// Get Visma Invoice
	async getVismaInvoice(clientNumber: string | number, invoiceNumber: string, page?: number, page_size?: number): Promise<void> {
		// Keep the shown invoice when only paging its lines, clear it when another invoice is opened
		this.updateState((state) => (
			state.activeRowVisma?.invoice?.InvoiceNumber === invoiceNumber ?
				{isVismaLoading: true} :
				{isVismaLoading: true, activeRowVisma: null}
		));
		try {
			if (typeof clientNumber === 'number') {
				clientNumber = clientNumber.toString();
			}
			const response = await fetch(`${vismaApiUrl}invoice/details`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify({
					client_number: clientNumber.toString(),
					invoice_number: invoiceNumber,
					page: page || 1,
					page_size: page_size || 10,
				}),
			});
			if (!response.ok) {
				throw new Error(`Error: ${response.status}`);
			}
			const json = await response.json();
			this.updateState(() => ({activeRowVisma: json}));
		} catch (err) {
			console.error('getVismaInvoice Error:', err);
			showError({
				message: getI18n().t('Error fetching invoice', {returnObjects: false}) as string,
			});
		} finally {
			this.updateState(() => ({isVismaLoading: false}));
		}
	}

	private downloadBlob(blob: Blob, filename: string): void {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();
		setTimeout(() => URL.revokeObjectURL(url), 0);
	}

	// Download reskonto visma
	async getVismaReskonto({
		data,
		onSuccess,
	}: {
		data: VismaRestkontoDownloadRequest;
		onSuccess?: (() => void) | undefined;
	}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await fetch(`${vismaApiUrl}reskonto/download`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify(data),
			});
			if (response.status === 200) {
				const blob = await response.blob();
				// Use the server's file name when it sends one
				const serverName = response.headers.get('Content-Disposition')?.match(/filename\*?="?([^";]+)"?/)?.[1];
				const extension = data.format === 'csv' ? 'csv' : 'xlsx';
				this.downloadBlob(blob, serverName ?? `${getI18n().t('Ledger account', {returnObjects: false})}_${data.account_id}.${extension}`);
				showSuccess({
					message: getI18n().t('Ledger account downloaded successfully', {returnObjects: false}) as string,
				});
				if (onSuccess) {
					onSuccess();
				}
			} else {
				showError({
					message: getI18n().t('Error downloading Ledger account', {returnObjects: false}) as string,
				});
			}
		} catch (err) {
			console.error('getVismaReskonto Error:', err);
			showError({
				message: getI18n().t('Error downloading Ledger account', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}

	// Download Latest Invoice
	async downloadInvoiceLatest(clientNumber: number, invoiceNumber: string): Promise<void> {
		try {
			const response = await fetch(`${vismaApiUrl}clients/${clientNumber}/invoices/${invoiceNumber}/download/latest`, {
				headers: getAliasHeader(),
				method: 'GET',
			});
			if (!response.ok) {
				throw new Error(`Error: ${response.status}`);
			}
			const blob = await response.blob();
			this.downloadBlob(blob, `${getI18n().t('Invoice', {returnObjects: false})}_${invoiceNumber}.pdf`);
		} catch (err) {
			console.error('downloadInvoiceLatest Error:', err);
			showError({
				message: `${getI18n().t('Error downloading invoice', {returnObjects: false}) as string} - ${invoiceNumber}`,
			});
		}
	}

	// Download Invoice
	async downloadInvoice(clientNumber: number, invoiceNumber: string): Promise<void> {
		try {
			const response = await fetch(`${vismaApiUrl}clients/${clientNumber}/invoices/${invoiceNumber}/download`, {
				headers: getAliasHeader(),
				method: 'GET',
			});
			if (!response.ok) {
				throw new Error(`Error: ${response.status}`);
			}
			const blob = await response.blob();
			this.downloadBlob(blob, `${getI18n().t('Invoice', {returnObjects: false})}_${invoiceNumber}.pdf`);
		} catch (err) {
			console.error('downloadInvoice Error:', err);
			showError({
				message: `${getI18n().t('Error downloading invoice', {returnObjects: false}) as string} - ${invoiceNumber}`,
			});
		}
	}
	// Change due date
	async vismaChangeDueDate(requestBody: ChangeDueDateRequest, onSuccess?: () => void): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const jsonResponse: ChangeDueDateResponse = await vismaChangeDueDate(requestBody);
			if (jsonResponse.status === '200' && jsonResponse.error_number === 0) {
				showSuccess({
					message: getI18n().t('Due date updated successfully. Old due date: {{oldDate}}, new due date: {{newDate}}', {
						oldDate: jsonResponse.old_due_date,
						newDate: jsonResponse.new_due_date,
						returnObjects: false,
					}) as string,
				});
				if (onSuccess) onSuccess();
			} else {
				showError({
					message: getI18n().t('Error updating due date', {returnObjects: false}) as string,
				});
			}
		} catch (err) {
			console.error('changeDueDate Error:', err);
			showError({
				message: getI18n().t('Error updating due date', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}

	// Send invoice PDF (mail)
	async sendVismaPdf({
		id,
		clientNr,
		emailReceiver,
		invoiceNr,
		language = 'NO',
		onSuccess,
	}: {
			id: string;
			clientNr: string;
			emailReceiver: string;
			invoiceNr: string;
			language: string;
			onSuccess?: (() => void) | undefined;
		}): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await fetch(`${vismaApiUrl}invoice/sendpdf`, {
				headers: getAliasHeader(),
				method: 'POST',
				body: JSON.stringify({
					account_id: id,
					client_nr: clientNr.toString(),
					email_receiver: emailReceiver,
					invoice_nr: invoiceNr,
					language: language,
				}),
			});
			const json = await response.json().catch(() => ({}));
			if (response.ok) {
				showSuccess({
					message: `${getI18n().t('Invoice sent successfully', {returnObjects: false}) as string}. ${json.message || ''}`,
				});
				if (onSuccess) {
					onSuccess();
				}
			} else {
				showError({
					message: `${getI18n().t('Error sending invoice', {returnObjects: false}) as string}. ${json.message || ''}`,
				});
			}
		} catch (err) {
			console.error('sendVismaPdf Error:', err);
			showError({
				message: getI18n().t('Error sending invoice', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}
	// User config. App and the event feed both ask for it on the first load, so they share one request
	getUserConfig(): Promise<void> {
		if (!this.userConfigRequest) {
			this.userConfigRequest = this.loadUserConfig().finally(() => {
				this.userConfigRequest = null;
			});
		}
		return this.userConfigRequest;
	}
	// Always a new request, so a reload after saving never reuses one that started before the save
	private async loadUserConfig(): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			const response = await threeSixtyApiCall<UserConfig>({
				endpoint: 'userconfig/all',
				method: 'POST',
			});
			if (response) {
				this.updateState(() => ({userConfig: response}));
			}

			if (
				response &&
				response?.configs &&
				Array.isArray(response.configs.account_feed_filter)
			) {
				// Merge, so the other page filters (smsFeed) are kept
				this.updateState((state) => ({
					pageFilters: {
						...state.pageFilters,
						eventFeed: response.configs.account_feed_filter as string[] || [],
					},
				}));
			}
		} catch (err) {
			console.error('getUserConfig Error:', err);
			showError({
				message: getI18n().t('Error fetching user config', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}
	async setUserConfig(
		key: string,
		value: string | number | boolean | unknown[] | string[] | DisplayPreferences | UserPreferences,
		onSuccess?: (() => void) | undefined
	): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			await threeSixtyApiCall<null>({
				endpoint: 'userconfig/set',
				method: 'POST',
				body: {
					key: key,
					value: value,
				},
			});
			await this.loadUserConfig();
			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('User setting saved successfully', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('setUserConfig Error:', err);
			showError({
				message: getI18n().t('Error saving user setting', {returnObjects: false}) as string,
			});
		} finally {
			doneLoading();
		}
	}

	// Update the setUserConfigBatch method signature
	async setUserConfigBatch(
		configs: Record<string, string | number | boolean | unknown[] | string[] | DisplayPreferences | UserPreferences>,
		onSuccess?: (() => void) | undefined
	): Promise<void> {
		const doneLoading = this.startLoading();
		try {
			// Send all config updates in parallel
			const updatePromises = Object.entries(configs).map(([key, value]) =>
				threeSixtyApiCall<null>({
					endpoint: 'userconfig/set',
					method: 'POST',
					body: {
						key: key,
						value: value,
					},
				})
			);

			// Rejects if any update fails
			await Promise.all(updatePromises);

			if (onSuccess) {
				onSuccess();
			}
			showSuccess({
				message: getI18n().t('User settings saved successfully', {returnObjects: false}) as string,
			});
		} catch (err) {
			console.error('setUserConfigBatch Error:', err);
			showError({
				message: getI18n().t('Error saving user settings', {returnObjects: false}) as string,
			});
		} finally {
			// Reload even after a failure: some of the parallel updates may have been saved
			await this.loadUserConfig();
			doneLoading();
		}
	}
}

export default ThreeSixtyStoreModel;